import { atomWithStorage } from 'jotai/utils';
import { z } from 'zod';
import { createSafeStorage } from '../utils/storage-utils';
import { DEFAULT_VOICE_TRANSCRIPTION_MODEL, isKnownVoiceModel } from '../voice/models-registry';

export const AppSettingsSchema = z.object({
  themeMode: z.enum(['system', 'light', 'dark']),
  // WHY: `.optional()` (not a required `z.boolean()` or a `.default()`) for two reasons:
  // 1. Legacy persisted values saved before this field existed must still `safeParse`
  //    successfully — an optional field simply parses as absent rather than failing the whole
  //    object and wiping the user's other settings (see createSafeStorage's safeParse-or-fallback).
  // 2. The existing setSettings call sites (StudioUIContext.tsx, MainLayout.tsx) replace the
  //    whole settings object rather than spreading previous state (`setSettings({ themeMode })`).
  //    A required field here would make those unrelated call sites fail to typecheck — outside
  //    this ticket's edit surface. `hapticsEnabled` therefore defaults at the read site
  //    (`useHaptics.ts`: `appSettings.hapticsEnabled ?? true`) rather than in the schema.
  // CONSTRAINT: because of (2), toggling theme via those call sites currently drops any explicit
  // hapticsEnabled value back to the implicit default. Not fixable within this ticket's file
  // surface — flag for whoever wires a haptics toggle into a settings UI.
  hapticsEnabled: z.boolean().optional()
});

export type AppSettings = z.infer<typeof AppSettingsSchema>;

const defaultSettings: AppSettings = {
  themeMode: 'light',
  hapticsEnabled: true
};

export const appSettingsAtom = atomWithStorage<AppSettings>(
  'appSettings',
  defaultSettings,
  createSafeStorage(AppSettingsSchema, defaultSettings)
);

// WHY: Voice dictation model selection (voice-input-engine). Persisted separately from `appSettings`
// so a voice-model change never round-trips the whole settings object (see the hapticsEnabled note
// above). CONSTRAINT: the stored value must stay a known registry slug — the schema's `.refine`
// makes createSafeStorage reset a stale/unknown slug (e.g. a model retired from the ladder) back to
// the default rather than feeding it to the transcription ladder.
export const VoiceTranscriptionModelSchema = z.string().refine(isKnownVoiceModel, {
  message: 'Unknown voice transcription model'
});

export const voiceTranscriptionModelAtom = atomWithStorage<string>(
  'voice-transcription-model-v1',
  DEFAULT_VOICE_TRANSCRIPTION_MODEL,
  createSafeStorage(VoiceTranscriptionModelSchema, DEFAULT_VOICE_TRANSCRIPTION_MODEL)
);
